/**
 * Wall-clock (floating) date+times — a `YYYY-MM-DD HH:MM:SS` string with **no
 * timezone**: what a clock reads at the location, e.g. a manual flight's
 * `departureTimeStr` ("departs 14:30 at the origin"). It is **never shifted** for
 * the viewer and never used to compute a duration (there is no zone to subtract).
 * HTML `datetime-local` inputs carry the same instant as `YYYY-MM-DDTHH:MM`.
 *
 * Pure: string in, string out. Display is rendered through UTC purely so the
 * shown numbers are exactly the stored numbers, in every timezone.
 */

/** datetime-local input `YYYY-MM-DDTHH:MM` → wall-clock `YYYY-MM-DD HH:MM:SS`. `''` for empty. */
export const inputToWallClock = (value: string): string => {
	if (!value) return ''
	const [datePart, timePart = '00:00'] = value.split('T')
	const [h, mi] = timePart.split(':')
	return `${datePart} ${(h || '00').padStart(2, '0')}:${(mi || '00').padStart(2, '0')}:00`
}

/** Wall-clock `YYYY-MM-DD HH:MM:SS` → datetime-local `YYYY-MM-DDTHH:MM`. `''` for empty/invalid. */
export const wallClockToInput = (str: string | null | undefined): string => {
	if (!str) return ''
	const match = str.match(/^(\d{4}-\d{2}-\d{2})( (\d{2}):(\d{2}))?/)
	if (!match) return ''
	const [, datePart, , h, mi] = match
	if (!h) return `${datePart}T00:00`
	return `${datePart}T${h}:${mi}`
}

/**
 * Wall-clock string → display: `Jan 15, 2:30 PM` (with time) or `Jan 15` (date
 * only). Returns the input unchanged if it isn't a wall-clock string, and `null`
 * for empty input. Rendered in UTC so the floating time is never shifted.
 */
export const formatWallClock = (str: string | null | undefined): string | null => {
	if (!str) return null
	const match = str.match(/^(\d{4})-(\d{2})-(\d{2})( (\d{2}):(\d{2}):(\d{2}))?$/)
	if (!match) return str
	const [, y, mo, d, , h, mi] = match
	const date = new Date(
		Date.UTC(Number(y), Number(mo) - 1, Number(d), h ? Number(h) : 0, mi ? Number(mi) : 0),
	)
	if (h) {
		return date.toLocaleString('en-US', {
			month: 'short',
			day: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
			hour12: true,
			timeZone: 'UTC',
		})
	}
	return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}
